import { useState } from "react";
import { fetchProducts, type Product, type ProductList } from "../api.ts";
import { ExternalIcon } from "../components/icons.tsx";
import { Card, LoadState, Note, PageHeader } from "../components/ui.tsx";
import { useLoader } from "../use-loader.ts";

export function ProductsPage() {
  const { state, reload } = useLoader<ProductList>(fetchProducts);

  return (
    <>
      <PageHeader
        title="プロダクト"
        description="ご利用いただけるプロダクトです。選択すると、ログインした状態で各プロダクトが開きます。"
      />
      <Card label="利用可能なプロダクト">
        {state.status === "loaded" ? (
          <>
            {state.data.incomplete ? (
              <Note tone="warning">
                一部のプロダクトの利用状況を確認できませんでした。表示されていないプロダクトがある場合は、時間をおいて再読み込みしてください。
              </Note>
            ) : null}
            {state.data.products.length === 0 ? (
              <p className="empty">利用できるプロダクトがありません。</p>
            ) : (
              <ul className="product-grid">
                {state.data.products.map((product) => (
                  <li key={product.id}>
                    <ProductCard product={product} />
                  </li>
                ))}
              </ul>
            )}
            <p className="card__footnote">
              ここにないプロダクトを利用したい場合は、担当コンサルタント・営業までご連絡ください。
            </p>
          </>
        ) : (
          <LoadState state={state.status} onRetry={reload} />
        )}
      </Card>
    </>
  );
}

/** アイコン画像があれば画像、無いか読み込めなければ表示名の頭文字 */
function ProductIcon({ product }: { product: Product }) {
  const [failed, setFailed] = useState(false);
  // iconUrl を返さないサーバー（アイコン対応前）でも頭文字にする
  const iconUrl = product.iconUrl ?? null;
  if (iconUrl !== null && !failed) {
    return (
      <span className="product-card__icon product-card__icon--image" aria-hidden="true">
        <img src={iconUrl} alt="" onError={() => setFailed(true)} />
      </span>
    );
  }
  return (
    <span className="product-card__icon" aria-hidden="true">
      {productInitials(product.name)}
    </span>
  );
}

function ProductCard({ product }: { product: Product }) {
  return (
    <a href={product.loginUrl} className="product-card" target="_blank" rel="noopener noreferrer">
      <div className="product-card__head">
        <ProductIcon product={product} />
        <div className="product-card__text">
          <div className="product-card__name">{product.name}</div>
          {product.description !== "" ? (
            <div className="product-card__description">{product.description}</div>
          ) : null}
        </div>
      </div>
      <div className="product-card__foot">
        <span className="product-card__host">{hostOf(product.loginUrl)}</span>
        <span className="product-card__open">
          開く
          <ExternalIcon size={14} />
          <span className="visually-hidden">（新しいタブで開きます）</span>
        </span>
      </div>
    </a>
  );
}

/** "SproNavigator" → "S"、"Sprocket Insights" → "SI"、"データ分析エージェント" → "デ" */
export function productInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2 && words.every((w) => /^[A-Za-z]/.test(w))) {
    return words
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("");
  }
  return (Array.from(name.trim())[0] ?? "?").toUpperCase();
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
